import { z } from "zod";
import { normalize } from "./assistant";

/*
 * Diktat zu Belegentwurf. Aus einem gesprochenen oder getippten Satz wie
 * „Angebot an Herrn Sülze, Stundenbasis 8 Stunden, Installationsrohr 32 Euro“ entsteht ein Vorschlag.
 * Nach festen Regeln, ohne KI-Dienst: Was nicht sicher erkannt wird, bleibt leer und gilt als „noch auszufüllen“.
 */

export type DictationType = "quote" | "order" | "invoice";

export interface DictationLine {
  name: string;
  quantity: number;
  unit: string;
  /** Netto in Cent. null = noch auszufüllen. */
  unitPriceCents: number | null;
  vatRate: 0 | 7 | 19;
  /** Artikel aus dem Stamm, wenn einer erkannt wurde. */
  itemId: string | null;
  /** Woher der Preis stammt: genannt im Diktat oder aus dem Artikelstamm. */
  priceFrom: "diktat" | "artikel" | null;
}

export interface DictationDraft {
  text: string;
  type: DictationType;
  customerId: string | null;
  /** Wie der Kunde im Diktat genannt wurde, auch wenn keiner gefunden wurde. */
  customerHeard: string | null;
  lines: DictationLine[];
}

export interface DictationCustomer {
  id: string;
  name: string;
  contactName: string | null;
}

export interface DictationItem {
  id: string;
  name: string;
  unit: string;
  priceCents: number;
  vatRate: number;
  kind: "service" | "product";
}

const TYPE_WORDS: [RegExp, DictationType][] = [
  [/\bangebot/i, "quote"],
  [/\bauftrag|\bauftragsbest/i, "order"],
  [/\brechnung/i, "invoice"],
];

const NUMBER_WORDS: Record<string, number> = {
  ein: 1, eine: 1, einen: 1, einem: 1, eins: 1, zwei: 2, drei: 3, vier: 4, fuenf: 5, sechs: 6, sieben: 7, acht: 8, neun: 9, zehn: 10,
  elf: 11, zwoelf: 12, zwanzig: 20, dreissig: 30, fuenfzig: 50, hundert: 100,
};

/** Zahlwörter am Wortanfang („acht Stunden“) in Ziffern wandeln, damit die Regeln nur Ziffern kennen. */
function digitize(text: string): string {
  return text.replace(/\b([A-Za-zÄÖÜäöüß]+)\b/g, (w) => {
    const n = NUMBER_WORDS[normalize(w)];
    return n !== undefined && normalize(w) !== "ein" && normalize(w) !== "eine" && normalize(w) !== "einen" && normalize(w) !== "einem" ? String(n) : w;
  });
}

const num = (s: string) => Number(s.replace(/\./g, "").replace(",", "."));
const NUMBER = String.raw`\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?|\d+(?:[.,]\d{1,2})?`;
const PRICE_RE = new RegExp(String.raw`(${NUMBER})\s*(?:€|euro\b|eur\b)|(?:€)\s*(${NUMBER})`, "i");
const HOURS_RE = new RegExp(String.raw`(${NUMBER})\s*(?:h\b|std\.?(?=\s|$)|stunden?\b)`, "i");
const QTY_RE = new RegExp(
  String.raw`^(${NUMBER})\s*(?:x\b|mal\b)?\s*(stück|stueck|stk\.?|st\.?|meter|m\b|kg|l\b|liter|rolle[n]?|packung(?:en)?|pack\b|set[s]?|paar|pauschal[e]?)?\s+(.+)$`,
  "i",
);
const FILLER = new Set(["auf", "basis", "stundenbasis", "arbeit", "arbeitszeit", "arbeitsstunden", "stundensatz", "zeit", "fuer", "a", "und", "mit", "je", "pro", "das", "den", "die", "der", "dann", "noch", "ausserdem", "plus"]);

function unitName(raw: string | undefined): string | null {
  if (!raw) return null;
  const n = normalize(raw);
  if (/^(stueck|stk|st)$/.test(n)) return "Stk";
  if (/^(meter|m)$/.test(n)) return "m";
  if (n === "kg" || n === "l") return n;
  if (n === "liter") return "l";
  if (/^rolle/.test(n)) return "Rolle";
  if (/^pack/.test(n)) return "Pack";
  if (/^set/.test(n)) return "Set";
  if (n === "paar") return "Paar";
  if (/^pauschal/.test(n)) return "pauschal";
  return null;
}

function tokens(s: string): string[] {
  return normalize(s).split(" ").filter(Boolean);
}

const sameWord = (a: string, b: string) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)));

/** Der Kunde, dessen Name oder Ansprechperson am besten zu den genannten Wörtern passt. Bei Gleichstand keiner. */
export function matchCustomer(heard: string, customers: DictationCustomer[]): string | null {
  const words = tokens(heard).filter((w) => !["herr", "herrn", "frau", "firma", "familie", "kunde", "kunden", "die", "den", "der", "dem"].includes(w));
  if (!words.length) return null;
  const scored = customers
    .map((c) => {
      const hay = [...tokens(c.name), ...tokens(c.contactName ?? "")];
      const hits = words.filter((w) => hay.some((h) => sameWord(h, w))).length;
      return { id: c.id, hits, miss: words.length - hits };
    })
    .filter((x) => x.hits > 0 && x.miss === 0)
    .sort((a, b) => b.hits - a.hits);
  if (!scored.length) return null;
  if (scored.length > 1 && scored[0]!.hits === scored[1]!.hits) return null;
  return scored[0]!.id;
}

/** Der Artikel, dessen Name am besten zum genannten Text passt. */
export function matchItem(text: string, items: DictationItem[]): DictationItem | null {
  const words = tokens(text);
  if (!words.length) return null;
  let best: { item: DictationItem; score: number } | null = null;
  for (const item of items) {
    const itemWords = tokens(item.name);
    if (!itemWords.length) continue;
    const found = itemWords.filter((w) => words.some((x) => sameWord(x, w))).length;
    // Alle Wörter des Artikels müssen vorkommen; oder alle genannten Wörter stecken im Artikelnamen.
    const allItem = found === itemWords.length;
    const allSaid = words.every((w) => itemWords.some((x) => sameWord(x, w)));
    if (!allItem && !allSaid) continue;
    const score = found * 2 + (allItem && allSaid ? 2 : 0);
    if (!best || score > best.score) best = { item, score };
  }
  return best?.item ?? null;
}

function cleanName(s: string): string {
  const kept = s
    .split(/\s+/)
    .filter((w) => w && !FILLER.has(normalize(w)))
    .join(" ")
    .replace(/^[\s:–-]+|[\s:–-]+$/g, "");
  return kept ? kept[0]!.toUpperCase() + kept.slice(1) : "";
}

/** Zerlegt das Diktat in einzelne Angaben: an Kommas, Semikolons, Zeilenumbrüchen und „und“ vor einer Zahl. */
function segments(text: string): string[] {
  return text
    .replace(/(\d),(\d)/g, "$1\u0000$2")
    .split(/[,;\n]+|\.\s+|\s+und\s+(?=\d)/i)
    .map((s) => s.replace(/\u0000/g, ",").trim())
    .filter(Boolean);
}

export function parseDictation(raw: string, customers: DictationCustomer[], items: DictationItem[]): DictationDraft {
  const text = digitize(raw.trim());
  let type: DictationType = "quote";
  let customerHeard: string | null = null;
  const lines: DictationLine[] = [];

  const workItem = items.find((i) => i.kind === "service" && /\b(arbeit|stunde|monteur|geselle|elektriker|servicetechniker|lohn)/.test(normalize(i.name)));

  for (const seg of segments(text)) {
    const typed = TYPE_WORDS.find(([re]) => re.test(seg));
    const isHeader = !!typed;
    if (typed) type = typed[1];
    // Kunde: „… an Herrn Sülze“, „für Familie Meier“, „Kunde Müller“.
    const cust = seg.match(/\b(?:an|für|fuer|bei|kunde|kunden)\s+(?:die\s+|den\s+)?(.+)$/i);
    if (cust && !customerHeard && (isHeader || /^(kunde|kunden)\b/i.test(seg))) {
      customerHeard = cust[1]!.replace(/[.!?]+$/, "").trim() || null;
      continue;
    }
    if (isHeader && !PRICE_RE.test(seg) && !HOURS_RE.test(seg)) continue;

    const priceM = seg.match(PRICE_RE);
    const price = priceM ? Math.round(num(priceM[1] ?? priceM[2]!) * 100) : null;
    let rest = priceM ? seg.replace(priceM[0], " ") : seg;

    const hoursM = rest.match(HOURS_RE);
    if (hoursM) {
      const hours = num(hoursM[1]!);
      rest = rest.replace(hoursM[0], " ");
      const given = cleanName(rest);
      const item = (given ? matchItem(given, items.filter((i) => i.kind === "service")) : null) ?? workItem ?? null;
      lines.push({
        name: item?.name ?? (given || "Arbeitszeit"),
        quantity: hours,
        unit: "Std",
        unitPriceCents: price ?? item?.priceCents ?? null,
        vatRate: ([0, 7, 19].includes(Number(item?.vatRate)) ? Number(item?.vatRate) : 19) as 0 | 7 | 19,
        itemId: item?.id ?? null,
        priceFrom: price !== null ? "diktat" : item ? "artikel" : null,
      });
      continue;
    }

    rest = rest.trim();
    if (!cleanName(rest) && price === null) continue;
    let quantity = 1;
    let unit: string | null = null;
    const q = rest.match(QTY_RE);
    if (q) {
      quantity = num(q[1]!);
      unit = unitName(q[2]);
      rest = q[3]!;
    }
    const name = cleanName(rest);
    if (!name) continue;
    const item = matchItem(name, items);
    lines.push({
      name: item?.name ?? name,
      quantity: quantity > 0 ? quantity : 1,
      unit: unit ?? item?.unit ?? "Stk",
      unitPriceCents: price ?? item?.priceCents ?? null,
      vatRate: ([0, 7, 19].includes(Number(item?.vatRate)) ? Number(item?.vatRate) : 19) as 0 | 7 | 19,
      itemId: item?.id ?? null,
      priceFrom: price !== null ? "diktat" : item ? "artikel" : null,
    });
  }

  return { text: raw.trim(), type, customerId: customerHeard ? matchCustomer(customerHeard, customers) : null, customerHeard, lines };
}

/** Was im Entwurf noch fehlt, in Worten für die Anzeige. Leer = der Entwurf kann zum Beleg werden. */
export function dictationMissing(d: Pick<DictationDraft, "customerId" | "lines">): string[] {
  const out: string[] = [];
  if (!d.customerId) out.push("Kunde");
  if (!d.lines.length) out.push("Positionen");
  d.lines.forEach((l, i) => {
    if (!l.name.trim()) out.push(`Bezeichnung von Position ${i + 1}`);
    if (l.unitPriceCents === null) out.push(`Preis von „${l.name || `Position ${i + 1}`}“`);
  });
  return out;
}

/** Gespeicherter Entwurf, wie er bearbeitet und zurückgeschickt wird. */
export const dictationDraftSchema = z.object({
  type: z.enum(["quote", "order", "invoice"]),
  customerId: z.uuid().nullable(),
  customerHeard: z.string().trim().max(200).nullable(),
  lines: z
    .array(
      z.object({
        name: z.string().trim().max(200),
        quantity: z.number().min(0.001).max(1_000_000),
        unit: z.string().trim().min(1).max(20),
        unitPriceCents: z.number().int().min(0).max(1_000_000_000).nullable(),
        vatRate: z.union([z.literal(0), z.literal(7), z.literal(19)]),
        itemId: z.uuid().nullable(),
        priceFrom: z.enum(["diktat", "artikel"]).nullable(),
      }),
    )
    .max(100),
});

export const dictationTextSchema = z.object({ text: z.string().trim().min(1, "Bitte sprechen oder schreiben Sie, was angeboten werden soll.").max(4000, "Der Text ist zu lang.") });

export interface DictationDraftInfo {
  id: string;
  text: string;
  createdAt: string;
  draft: DictationDraft;
  missing: string[];
}
